/**
 * ReportPreview — modal preview for a generated report.
 *
 * Renders the generated artefact inline using the same viewer the Documents
 * tab uses (DocumentPreviewPopupCN): react-pdf for PDF (canvas + Prev/Next),
 * mammoth for DOCX, and the sheet viewer for XLSX. HTML reports are shown in
 * an iframe (native HTML scrolls fine inside the modal).
 *
 * Why not a native-PDF <iframe>: the browser's built-in PDF plugin viewer is
 * not reliably scrollable/navigable when embedded in an overlaid modal, so the
 * preview looked rendered but couldn't be paged. DocumentPreviewPopupCN renders
 * into the DOM instead, which stays fully interactive inside the dialog.
 *
 * The file itself is still fetched through C2's /reports/:id/download endpoint
 * via the auth-aware blob thunk (MYD-344) and handed to the viewer as a blob
 * URL, so the auth-token + server-side re-signing path is unchanged.
 *
 * Linear: MYD-197 (C4).
 */

import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { ExternalLink, AlertCircle, Loader2, FileType, FileText } from 'lucide-react'
import DocumentPreviewPopupCN from 'components/popups/DocumentPreviewPopupCN'
import { fetchReportBlobThunk } from 'slices/reportSlice'
import type { ReportRow, ReportScope } from 'services/reports/reportService'

interface ReportPreviewProps {
	open: boolean
	onClose: () => void
	scope: ReportScope
	entityId: string
	report: ReportRow | null
}

const ReportPreview = ({ open, onClose, scope, entityId, report }: ReportPreviewProps) => {
	const dispatch = useDispatch()
	// MYD-344: fetch the report file via the auth-aware thunk (which CRA's proxy
	// forwards) and turn it into a blob URL the inline viewer can load. Going
	// through the slice thunk keeps the auth-token path identical to every other
	// report endpoint.
	const [blobUrl, setBlobUrl] = useState<string | null>(null)
	const [blobError, setBlobError] = useState<string | null>(null)

	useEffect(() => {
		setBlobError(null)
	}, [report?._id])

	useEffect(() => {
		let cancelled = false
		let createdUrl: string | null = null
		if (!open || !report || report.status !== 'READY' || !report.fileUrl) {
			return
		}

		// Always go through the backend download endpoint via the blob thunk. The
		// backend re-signs / streams the file server-side and 302-redirects axios
		// to the signed URL; using the absolute fileUrl directly 401s on some
		// storage ACLs.
		;(async () => {
			try {
				// @ts-ignore — thunks
				const result: any = await dispatch(
					fetchReportBlobThunk({ scope, entityId, reportId: report._id }),
				)
				if (cancelled) return
				if (result?.error) {
					setBlobError(result.payload || 'Failed to load report')
					return
				}
				const blob: Blob = result.payload
				createdUrl = URL.createObjectURL(blob)
				setBlobUrl(createdUrl)
			} catch (err: any) {
				if (cancelled) return
				setBlobError(err?.message || 'Failed to load report')
			}
		})()
		return () => {
			cancelled = true
			if (createdUrl) URL.revokeObjectURL(createdUrl)
			setBlobUrl(null)
		}
	}, [dispatch, open, scope, entityId, report])

	if (!report) return null

	const fmt = (report.format || '').toUpperCase()
	const previewType = fmt.toLowerCase() // 'pdf' | 'docx' | 'html' | 'xlsx'
	const isHtml = fmt === 'HTML'
	const isDocx = fmt === 'DOCX'
	const isReady = report.status === 'READY' && Boolean(report.fileUrl)
	const title = report.templateName || report.templateKey || 'Report'

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="max-w-5xl">
				<DialogHeader>
					<div className="min-w-0">
						<DialogTitle className="truncate flex items-center gap-2">
							{isDocx ? (
								<FileType className="w-5 h-5 text-blue-500" />
							) : (
								<FileText className="w-5 h-5 text-[#0080fe]" />
							)}
							{title}
						</DialogTitle>
						<DialogDescription className="truncate">
							{fmt} · {new Date(report.generatedAt).toLocaleString()}
						</DialogDescription>
					</div>
				</DialogHeader>

				{/* Body — fixed 80vh (matches the Documents-tab preview) so the
				    embedded viewer gets a real height and stays interactive. */}
				<div className="h-[80vh] bg-gray-50">
					{!isReady ? (
						<div className="h-full flex flex-col items-center justify-center text-center px-6">
							<AlertCircle className="w-10 h-10 text-amber-500 mb-2" />
							<p className="text-sm text-gray-700 font-medium">
								This report isn't ready to preview.
							</p>
							<p className="text-xs text-gray-500 mt-1 max-w-sm">
								Status: <span className="font-mono">{report.status}</span>
								{report.error && <> — {report.error}</>}
							</p>
						</div>
					) : blobError ? (
						<div className="h-full flex flex-col items-center justify-center text-center px-6">
							<AlertCircle className="w-10 h-10 text-red-500 mb-2" />
							<p className="text-sm text-gray-700 font-medium">
								Couldn't load the report file.
							</p>
							<p className="text-xs text-gray-500 mt-1 max-w-sm">{blobError}</p>
						</div>
					) : !blobUrl ? (
						<div className="h-full flex items-center justify-center">
							<div className="flex items-center gap-2 text-gray-500 text-sm">
								<Loader2 className="w-4 h-4 animate-spin" />
								Loading {fmt}…
							</div>
						</div>
					) : isHtml ? (
						<iframe
							key={report._id}
							title={title}
							src={blobUrl}
							className="w-full h-full bg-white border-0"
						/>
					) : (
						<DocumentPreviewPopupCN uri={blobUrl} fileType={previewType} fileName={title} />
					)}
				</div>

				<DialogFooter>
					<button
						type="button"
						disabled={!blobUrl}
						onClick={() => blobUrl && window.open(blobUrl, '_blank', 'noopener,noreferrer')}
						className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<ExternalLink className="w-4 h-4" />
						Open in new tab
					</button>
					<Button variant="ghost" onClick={onClose}>
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default ReportPreview
